import { CombinedGraphQLErrors } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { type ReactNode, Suspense } from 'react';

import { Loader } from '@repo/ui/components/elements/Loader/Loader';
import { MainContentWrapper } from '@repo/ui/components/MainContentWrapper/MainContentWrapper';

import { GetMyPartnerDocument } from '../graphql/generated';
import { PartnerHeader } from './partner-header';
import { PartnerSidebar } from './partner-sidebar';
import { CapabilitiesUnavailable } from './permissions/capabilities-unavailable';
import { PartnerCapabilitiesProvider, useCapabilitiesUnavailable } from './permissions/partner-capabilities-context';

interface CapabilityGuardedContentProps {
  children: ReactNode;
  onRetry: () => Promise<boolean>;
}

/**
 * Reads the capability state from inside the provider so the page swap and
 * the gates below it decide from one value.
 *
 * Only an outage replaces the page. An account with no partner keeps its
 * routes — settings included — and meets each page's own empty handling,
 * which is what it did before capabilities began denying by default; taking
 * the whole app away from an admin who opened it would be a bigger change
 * than this PR is making.
 */
function CapabilityGuardedContent({ children, onRetry }: CapabilityGuardedContentProps) {
  if (useCapabilitiesUnavailable()) {
    return <CapabilitiesUnavailable onRetry={onRetry} />;
  }
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

/**
 * Whether the failure was the backend refusing for want of a partner rather
 * than being unreachable. Such an account is not in an outage: nothing is
 * broken and a retry would change nothing.
 *
 * Keyed on the code the backend states, not on FORBIDDEN alone and not on
 * the sentence. Denying capabilities by default makes FORBIDDEN a routine
 * error inside this query — a partner type without `canViewLicenses`
 * produces one from the licence field resolver — and reading those as "no
 * partner" would tell a manager who has one that they do not. Matching the
 * message instead would put a screen's behaviour at the mercy of a reworded
 * sentence.
 *
 * Read through `CombinedGraphQLErrors` because Apollo 4 carries the GraphQL
 * errors on `errors`; a `graphQLErrors` lookup finds nothing here and would
 * show every unassigned account the outage panel.
 */
export function isNoPartnerError(error: unknown): boolean {
  if (!CombinedGraphQLErrors.is(error)) return false;
  return error.errors.some(graphQLError => graphQLError.extensions?.code === 'PARTNER_MANAGER_NOT_ASSIGNED');
}

export function PartnerLayout({ children }: { children: ReactNode }) {
  // Layout-level GetMyPartner fetch primes the Apollo cache for every page
  // (no extra round-trip — useQuery dedupes) and gives the sidebar +
  // capability-gated surfaces a single source for `partner.capabilities`.
  // `errorPolicy: 'all'` so a partial failure (e.g. license resolver
  // throws) still hands us partner + capabilities for navigation.
  //
  // Deliberately without `notifyOnNetworkStatusChange`: flipping `loading`
  // during a refetch would clear `error` mid-flight, the provider would stop
  // reporting the outage, and the panel would replace itself with a loader
  // the moment the user asked it to try again. The panel tracks its own
  // retry, so it stays put and only its button changes.
  const { data, loading, error, refetch } = useQuery(GetMyPartnerDocument, { errorPolicy: 'all' });

  const capabilities = data?.myPartner?.capabilities;

  // Reports whether the refetch recovered; the panel owns what it does about
  // that, so no retry state has to be reset from here.
  const handleRetry = async (): Promise<boolean> => {
    const result = await refetch();
    return result.data?.myPartner?.capabilities !== undefined;
  };

  return (
    <PartnerCapabilitiesProvider
      value={capabilities}
      loading={loading}
      error={Boolean(error)}
      detached={isNoPartnerError(error)}
    >
      <PartnerHeader />
      <div style={{ backgroundColor: 'var(--background-main)' }}>
        <PartnerSidebar />
        <MainContentWrapper>
          <CapabilityGuardedContent onRetry={handleRetry}>{children}</CapabilityGuardedContent>
        </MainContentWrapper>
      </div>
    </PartnerCapabilitiesProvider>
  );
}
